import { faCopy } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { AxiosError } from 'axios';
import { useContext, useState } from 'react';

import { Color } from '@/constants/color';
import AppContext from '@/contexts/appContext';
import { useShortenLink } from '@/hooks/useShortenLink';
import { Banner } from '@/ui';
import Button from '@/ui/components/Button';
import mapAxiosErrorMessages from '@/utils/mapAxiosErrorMessages';

import { Props } from './types';

const GenerateShortenedLinkButton = ({ hasMissingFields, link }: Props) => {
  const { addMessage } = useContext(AppContext);
  const [linkGenerated, setLinkGenerated] = useState<string | null>(null);

  const copyToClipboard = async (link: string) => {
    try {
      await navigator.clipboard.writeText(link);
      addMessage({
        level: 'success',
        text: 'The link has been copied to your clipboard!'
      });
    } catch {
      addMessage({
        level: 'warning',
        text: 'There was an error copying the link to your clipboard. Please copy it manually.'
      });
    }
  };

  const { mutate: shortenLink, isPending: isShorteningLink } = useShortenLink({
    onSuccess: async ({ shortened_url }) => {
      await copyToClipboard(shortened_url);
      setLinkGenerated(shortened_url);
    },
    onError: (error: AxiosError) => addMessage(mapAxiosErrorMessages(error))
  });

  const handleClick = () => {
    if (link) shortenLink(link);
  };

  return (
    <div className="relative">
      {!linkGenerated && (
        <Button
          className="sm:w-auto w-full"
          disabled={hasMissingFields || !!linkGenerated}
          loading={isShorteningLink}
          title={linkGenerated ? 'Link already copied to the clipboard' : 'Generate shortened link'}
          onClick={handleClick}>
          Generate shortened link
        </Button>
      )}

      {!!linkGenerated && (
        <Banner color="amber" label={linkGenerated}>
          <Button
            ariaLabel="Copy to clipboard"
            className="ml-1 shrink-0"
            color={Color.WHITE}
            onClick={() => copyToClipboard(linkGenerated)}
            size="xs"
            title="Copy to clipboard">
            <FontAwesomeIcon className="text-gray-500" icon={faCopy} />
          </Button>
        </Banner>
      )}
    </div>
  );
};

export default GenerateShortenedLinkButton;
